import { LeftOutlined } from '@ant-design/icons'
import { Button, DatePicker, Flex, Table } from 'antd'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useSimeTbServiceGetSimeTbTreatmentPlanListEndpoint } from 'src/api/queries'
import { TreatmentPlanSortBy3 } from 'src/api/requests'
import { LoadingScreen, PageBreadcrumbs, Select, Text } from 'src/components'
import { usePersonBreadcrumb } from 'src/features/persons/hooks'
import { useStyles } from 'src/features/persons/styles'
import { useTablePagination } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import {
  getOrderingDirection,
  normalizeOrdering,
  resetPagination
} from 'src/utils'

import {
  useTreatmentColumns,
  useTreatmentConfig
} from '../PersonTreatmentsSection'

export const SimeTBDetailsList = () => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const navigate = useNavigate()

  const { id: personId, simeTBPersonId } = useParams()

  const pagination = useTablePagination()

  const { options } = useTreatmentConfig()
  const { columns } = useTreatmentColumns(false, true)

  const [date, setDate] = useState<string[]>([])
  const [ordering, setOrdering] = useState('')

  const { breadcrumbItems, fullName, isLoadingPerson } =
    usePersonBreadcrumb(personId)

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(ordering && {
      sortBy: normalizeOrdering(ordering) as TreatmentPlanSortBy3,
      sortDirection: getOrderingDirection(ordering)
    }),
    ...(date?.[0] && date?.[1] && { startDate: date?.[0], endDate: date?.[1] }),
    personId: simeTBPersonId
  }

  const { data, isLoading } =
    useSimeTbServiceGetSimeTbTreatmentPlanListEndpoint(queryParams)

  const onBackClick = () => {
    navigate(paths.patients.details(personId))
  }

  const onDateChange = (datesString: string[]) => {
    setDate(datesString)
    resetPagination(pagination)
  }

  const onSelect = (val: string) => {
    setOrdering(val)
    resetPagination(pagination)
  }

  if (isLoadingPerson) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs title={fullName} items={breadcrumbItems}>
      <Flex vertical gap={16}>
        <Button
          icon={<LeftOutlined />}
          className="fit-content"
          onClick={onBackClick}
        >
          {t('common.back')}
        </Button>

        <Flex justify="space-between" wrap="wrap" gap={8}>
          <Text className={styles.text}>{t('persons.sime_tb_treatments')}</Text>

          <Flex gap={8}>
            <DatePicker.RangePicker
              format={[dayMonthYearBackSlash, dayMonthYearBackSlash]}
              onChange={(_, datesString) => onDateChange(datesString)}
              placeholder={[
                t('notifications.from_date'),
                t('notifications.to_date')
              ]}
            />

            <Select onSelect={onSelect} options={options} />
          </Flex>
        </Flex>

        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
